import { Component, OnInit, DestroyRef, inject, signal } from '@angular/core';

import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { UserService } from '../../../../shared/services/user.service';
import { UserRole } from '#backend/common/user-types';
import { EnhancedLoadingService } from '../../../../shared/services/enhanced-loading.service';
import { SmartOrpcClientService } from '../../../../shared/services/smart-orpc-client.service';
import type { contractInput, contractOutput } from '#backend/contract';
import { SnackbarService } from '../../../../shared/services/snackbar.service';

interface RoleOption {
  value: UserRole;
  label: string;
}

interface CompanyOption {
  value: number;
  label: string;
}

type LoadedUser = contractOutput['users']['get'];
type UpdateUserPayload = contractInput['users']['update'];
type UpdateUserChanges = Partial<Omit<UpdateUserPayload, 'id'>>;

@Component({
  selector: 'app-user-create',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSnackBarModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './user-create.component.html',
  styleUrls: ['./user-create.component.scss'],
})
export class UserCreateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private snackBar = inject(SnackbarService);
  private userService = inject(UserService);
  private orpc = inject(SmartOrpcClientService).safeClient;
  private destroyRef = inject(DestroyRef);
  isLoading = toSignal(inject(EnhancedLoadingService).isLoad$);

  userForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.minLength(3)]],
    email: ['', [Validators.required, Validators.email]],
    role: new FormControl<UserRole>(UserRole.VISUALIZADOR_CLIENTE, {
      validators: [Validators.required],
      nonNullable: true,
    }),
    organizationId: new FormControl<number | null>(null, {
      validators: [Validators.required],
    }),
  });

  isSubmitting = signal(false);

  mode: 'create' | 'edit' | 'view' = 'create';
  userId: string | null = null;
  existingUser: LoadedUser | null = null;
  allowRoleChange = false;
  allowOrganizationSelection = false;

  loggedInUserRole: UserRole | null = null;
  loggedInUserOrgId: number | null = null;
  loggedInUserName?: string;
  selectedRole: UserRole | '' = '';

  roleOptions: RoleOption[] = [];
  companies: CompanyOption[] = [];

  async ngOnInit(): Promise<void> {
    await this.initialize();
  }

  get isGeoambienteUser(): boolean {
    return (
      this.loggedInUserRole === UserRole.ADMIN_GEOAMBIENTE ||
      this.loggedInUserRole === UserRole.OPERADOR_GEOAMBIENTE
    );
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  async onSubmit(): Promise<void> {
    if (this.isViewMode) {
      return;
    }

    if (this.userForm.invalid) {
      this.markFormGroupTouched(this.userForm);
      return;
    }

    const formValue = this.userForm.getRawValue();
    const selectedRole = formValue.role as UserRole;
    const requiresOrganization =
      this.requiresOrganizationSelection(selectedRole);
    const defaultOrganization =
      this.existingUser?.organizationId ?? this.loggedInUserOrgId ?? null;
    const organizationIdRaw = requiresOrganization
      ? (formValue.organizationId ?? null)
      : (formValue.organizationId ?? defaultOrganization);
    let organizationId = organizationIdRaw ?? null;

    if (!requiresOrganization) {
      organizationId = null;
    }

    if (requiresOrganization && !organizationId) {
      this.snackBar.alert('Selecione uma empresa para o usuário.');
      return;
    }

    if (
      this.mode === 'create' &&
      this.isGeoambienteUser &&
      requiresOrganization &&
      typeof organizationId === 'number'
    ) {
      const limitStatus = await this.checkUserLimit(organizationId);
      if (limitStatus?.hasReachedLimit) {
        return;
      }
    }

    if (this.isEditMode) {
      await this.updateUser(formValue);
      return;
    }

    await this.createUser({
      name: formValue.name,
      email: formValue.email,
      role: selectedRole,
      organizationId,
    });
  }

  onCancel(): void {
    this.router.navigate(['/admin/users']);
  }

  getErrorMessage(fieldName: string): string {
    const control = this.userForm.get(fieldName);

    if (control?.hasError('required')) {
      return 'Este campo é obrigatório';
    }

    if (control?.hasError('email')) {
      return 'E-mail inválido';
    }

    if (control?.hasError('minlength')) {
      return 'Nome deve ter no mínimo 3 caracteres';
    }

    return '';
  }

  private async initialize(): Promise<void> {
    try {
      this.resolveRouteContext();

      const authUser = await this.userService.requireUser();
      this.loggedInUserRole = authUser.role;
      this.loggedInUserOrgId = authUser.organizationId ?? null;
      this.loggedInUserName = authUser.organizationName ?? undefined;

      await Promise.all([
        this.userId ? this.loadExistingUser() : undefined,
        this.loadOrganizations(),
      ]);

      this.setupPermissions();
      this.setupRoleOptions();
      this.initForm();
      this.applyModeSettings();
    } catch (error) {
      console.error('[UserCreate] Falha ao inicializar formulário', error);
      this.snackBar.alert('Não foi possível carregar os dados iniciais.');
      if (this.mode !== 'create') {
        void this.router.navigate(['/admin/users']);
      }
    }
  }

  private async loadOrganizations(): Promise<void> {
    if (this.isGeoambienteUser) {
      const {
        data: response,
        error,
        isDefined,
      } = await this.orpc.organization.list({});

      if (error) {
        console.error('[UserCreate] Falha ao carregar organizações', error);
        this.snackBar.alert('Não foi possível carregar a lista de empresas.');
        this.companies = [];
      } else {
        this.companies = response.items.map((organization) => ({
          value: organization.id!,
          label: organization.name,
        }));
      }
    } else if (this.loggedInUserOrgId) {
      this.companies = [
        {
          value: this.loggedInUserOrgId,
          label: this.loggedInUserName ?? 'Minha organização',
        },
      ];
    }

    if (
      this.existingUser?.organizationId &&
      !this.companies.some(
        (company) => company.value === this.existingUser?.organizationId,
      )
    ) {
      this.companies = [
        ...this.companies,
        {
          value: this.existingUser.organizationId,
          label:
            this.existingUser.organizationName ??
            this.loggedInUserName ??
            'Empresa vinculada',
        },
      ];
    }
  }

  private resolveRouteContext(): void {
    const routePath = this.route.snapshot.routeConfig?.path ?? '';
    const idParam = this.route.snapshot.paramMap.get('id');

    if (routePath === 'create') {
      this.mode = 'create';
      this.userId = null;
      return;
    }

    if (idParam) {
      this.userId = idParam;
      if (this.userId == '') {
        this.userId = null;
      }
    }

    if (routePath.includes('edit')) {
      this.mode = 'edit';
    } else if (this.userId) {
      this.mode = 'view';
    } else {
      this.mode = 'create';
    }
  }

  private async loadExistingUser(): Promise<void> {
    if (!this.userId) {
      return;
    }

    const {
      data: user,
      error,
      isDefined,
    } = await this.orpc.users.get({ id: this.userId });
    if (error) {
      this.snackBar.alert(isDefined ? error.message : 'Usuário não encontrado');
    } else {
      this.existingUser = user;
      this.selectedRole = user.role;
    }
  }

  private setupPermissions(): void {
    if (this.mode === 'create') {
      this.allowRoleChange = true;
      this.allowOrganizationSelection = this.isGeoambienteUser;
      return;
    }

    if (!this.existingUser) {
      this.allowRoleChange = false;
      this.allowOrganizationSelection = false;
      return;
    }

    if (!this.isEditMode) {
      this.allowRoleChange = false;
      this.allowOrganizationSelection = false;
      return;
    }

    const canEdit = this.canEditTargetUser();
    this.allowRoleChange = canEdit;
    this.allowOrganizationSelection = canEdit && this.isGeoambienteUser;
  }

  private applyModeSettings(): void {
    if (!this.userForm) {
      return;
    }

    const roleControl = this.userForm.get('role');
    if (roleControl) {
      if (this.allowRoleChange) {
        roleControl.enable({ emitEvent: false });
      } else {
        roleControl.disable({ emitEvent: false });
      }
    }

    const organizationControl = this.userForm.get('organizationId');
    if (organizationControl) {
      if (this.allowOrganizationSelection && !this.isViewMode) {
        organizationControl.enable({ emitEvent: false });
      } else {
        organizationControl.disable({ emitEvent: false });
      }
    }

    if (this.isViewMode) {
      this.userForm.disable({ emitEvent: false });
    }
  }

  private canEditTargetUser(): boolean {
    if (!this.existingUser) {
      return false;
    }

    const canManageUsers =
      this.isGeoambienteUser ||
      this.loggedInUserRole === UserRole.ADMIN_CLIENTE;

    if (!canManageUsers) {
      return false;
    }

    if (this.isGeoambienteUser) {
      return true;
    }

    if (this.loggedInUserOrgId === null) {
      return false;
    }

    return this.existingUser.organizationId === this.loggedInUserOrgId;
  }

  private setupRoleOptions(): void {
    if (this.isGeoambienteUser) {
      this.roleOptions = [
        {
          value: UserRole.ADMIN_GEOAMBIENTE,
          label: 'Administrador Geoambiente',
        },
        { value: UserRole.OPERADOR_GEOAMBIENTE, label: 'Operador Geoambiente' },
        { value: UserRole.ADMIN_CLIENTE, label: 'Administrador' },
        { value: UserRole.VISUALIZADOR_CLIENTE, label: 'Visualizador' },
      ];
    } else {
      this.roleOptions = [
        { value: UserRole.ADMIN_CLIENTE, label: 'Administrador' },
        { value: UserRole.VISUALIZADOR_CLIENTE, label: 'Visualizador' },
      ];
    }

    if (this.existingUser) {
      this.selectedRole = this.existingUser.role;
    }
  }

  private initForm(): void {
    const initialName = this.existingUser?.name ?? '';
    const initialEmail = this.existingUser?.email ?? '';
    const initialRole: UserRole =
      this.existingUser?.role ?? UserRole.VISUALIZADOR_CLIENTE;
    const initialOrganization =
      this.existingUser?.organizationId ?? this.loggedInUserOrgId ?? null;

    this.selectedRole = initialRole;
    this.userForm.setValue({
      name: initialName,
      email: initialEmail,
      role: initialRole,
      organizationId: initialOrganization,
    });
    if (this.isEditMode || this.isViewMode)
      this.userForm.controls.email.disable();

    const roleControl = this.userForm.controls.role;
    roleControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((role) => {
        this.handleRoleChange(role ?? '');
      });

    this.handleRoleChange(roleControl?.value ?? initialRole ?? '');
  }

  private async checkUserLimit(organizationId: number): Promise<{
    hasReachedLimit: boolean;
    maxUsers: number;
    activeUsers: number;
  } | null> {
    const { data, error, isDefined } = await this.orpc.users.checkLimit({
      organizationId,
    });
    if (error) {
      this.snackBar.alert(
        error.message ?? 'Falha ao verificar limite de usuários',
      );
      return null;
    } else {
      const { hasReachedLimit, activeUsers, maxUsers } = data;

      if (hasReachedLimit) {
        this.snackBar.alert(
          `Atenção: a empresa já possui ${activeUsers} usuários ativos (limite ${maxUsers}).`,
          undefined,
          'warning-snackbar',
        );
      }

      return { hasReachedLimit, maxUsers, activeUsers };
    }
  }

  private async createUser(payload: {
    name: string;
    email: string;
    role: UserRole;
    organizationId: number | null;
  }): Promise<void> {
    this.isSubmitting.set(true);
    const { organizationId, ...rest } = payload;
    const {
      data: result,
      error,
      isDefined,
    } = await this.orpc.users.create({
      ...rest,
      organizationId: organizationId ?? undefined,
    });
    if (error) {
      console.error('[UserCreate] Falha ao criar usuário', error);

      this.snackBar.alert(
        `Não foi possível cadastrar o usuário.\n${isDefined ? error.message : ''}`,
        -1,
      );
    }
    if (result) {
      this.snackBar.alert(
        'Usuário cadastrado com sucesso!',
        3000,
        'success-snackbar',
      );
      this.router.navigate(['/admin/users']);
    }
    this.isSubmitting.set(false);
  }

  private async updateUser(formValue: {
    name: string;
    email: string;
    role: UserRole | '';
    organizationId?: number | null;
  }): Promise<void> {
    if (!this.existingUser || !this.userId) {
      return;
    }

    const changes: UpdateUserChanges = {};

    if (formValue.name && formValue.name !== this.existingUser.name) {
      changes.name = formValue.name;
    }

    if (
      this.allowRoleChange &&
      formValue.role &&
      formValue.role !== this.existingUser.role
    ) {
      changes.role = formValue.role;
    }

    if (this.allowOrganizationSelection) {
      changes.organizationId = formValue.organizationId;
    }

    if (Object.keys(changes).length === 0) {
      this.snackBar.alert('Nenhuma alteração foi realizada.', 3000);
      return;
    }

    this.isSubmitting.set(true);
    try {
      const {
        data: updated,
        error,
        isDefined,
      } = await this.orpc.users.update({
        id: this.userId,
        ...changes,
      });
      if (error) {
        console.error('[UserCreate] Falha ao atualizar usuário', error);
        this.snackBar.alert(
          isDefined ? error.message : 'Não foi possível atualizar o usuário.',
        );
      }

      if (updated) {
        this.snackBar.alert(
          'Usuário atualizado com sucesso!',
          3000,
          'success-snackbar',
        );
        await this.router.navigate(['/admin/users']);
      }
    } catch (error) {
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private handleRoleChange(role: UserRole): void {
    this.selectedRole = role;

    const organizationControl = this.userForm.controls.organizationId;

    const requiresSelection = this.requiresOrganizationSelection(
      this.selectedRole,
    );
    const existingOrganization = this.existingUser?.organizationId ?? null;
    const defaultOrganization =
      existingOrganization ?? this.loggedInUserOrgId ?? null;

    if (!requiresSelection) {
      organizationControl.clearValidators();
      organizationControl.disable({ emitEvent: false });
      const fallbackOrganization = this.isGeoambienteUser
        ? null
        : defaultOrganization;
      organizationControl.setValue(fallbackOrganization, {
        emitEvent: false,
      });
      organizationControl.updateValueAndValidity({ emitEvent: false });
      return;
    }

    if (this.allowOrganizationSelection && !this.isViewMode) {
      organizationControl.setValidators(Validators.required);
      organizationControl.enable({ emitEvent: false });

      if (this.isEditMode && existingOrganization !== null) {
        organizationControl.setValue(existingOrganization, {
          emitEvent: false,
        });
      } else if (organizationControl.value === undefined) {
        organizationControl.setValue(null, { emitEvent: false });
      }

      organizationControl.updateValueAndValidity({ emitEvent: false });
      return;
    }

    organizationControl.clearValidators();
    organizationControl.disable({ emitEvent: false });
    organizationControl.setValue(defaultOrganization, {
      emitEvent: false,
    });
    organizationControl.updateValueAndValidity({ emitEvent: false });
  }

  private requiresOrganizationSelection(role: UserRole | ''): boolean {
    return !this.isGeoProfile(role);
  }

  private isGeoProfile(role: UserRole | ''): boolean {
    return (
      role === UserRole.ADMIN_GEOAMBIENTE ||
      role === UserRole.OPERADOR_GEOAMBIENTE
    );
  }

  get isGeoRoleSelected(): boolean {
    return this.isGeoProfile(this.selectedRole);
  }

  get shouldRequestOrganization(): boolean {
    return this.requiresOrganizationSelection(this.selectedRole);
  }

  get showSubmitButton(): boolean {
    return !this.isViewMode;
  }

  get submitButtonLabel() {
    if (this.isSubmitting()) {
      return this.isEditMode ? 'Salvando...' : 'Cadastrando...';
    }

    return this.isEditMode ? 'Salvar alterações' : 'Cadastrar Usuário';
  }

  get cancelButtonLabel(): string {
    return this.isViewMode ? 'Voltar' : 'Cancelar';
  }

  get disableSubmitButton(): boolean {
    if (!this.showSubmitButton) {
      return true;
    }

    const requiresOrg = this.shouldRequestOrganization;
    const missingOrganizationOptions =
      this.isGeoambienteUser && requiresOrg && this.companies.length === 0;

    return (
      this.isSubmitting() || this.isLoading() || missingOrganizationOptions
    );
  }

  get organizationInfoText(): string {
    const label = this.resolveOrganizationLabel();
    if (this.mode === 'create') {
      return `Usuário será criado automaticamente ${label}.`;
    }

    return `Usuário está vinculado ${label}.`;
  }

  private resolveOrganizationLabel(): string {
    const selectedId = this.userForm.controls.organizationId.getRawValue();
    if (selectedId) {
      return `à organização ${this.companies.find((c) => c.value == selectedId) ?? '#${this.userForm.controls.organizationId}'}`;
    }

    return 'à Geoambiente';
  }
}
